import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';

import { LoggerService } from '@spaces-ui/logger';

import { isGif, playGifInCanvas } from '../gif-canvas-player';
import { ReducedMotionFacade, REDUCED_MOTION_MAX_LOOPS } from '../reduced-motion-facade';

@Component({
  selector: 'spaces-animated-image',
  templateUrl: './animated-image.html',
  styleUrls: ['./animated-image.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnimatedImage {
  private readonly reducedMotionFacade = inject(ReducedMotionFacade);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loggerService = inject(LoggerService);

  public readonly src = input.required<string>();
  public readonly alt = input<string>('');
  /** Optional MIME type hint for callers whose `src` is a blob: URL or otherwise opaque. */
  public readonly mimeType = input<string | undefined>(undefined);

  protected readonly canvasMode = computed(
    () => this.reducedMotionFacade.isActive() && isGif(this.src(), this.mimeType()),
  );

  // Tracks the URL of the most recent decode failure. When this matches the
  // current src, we fall back to <img> instead of an empty <canvas>.
  private readonly failedSrc = signal<string | null>(null);

  protected readonly showCanvas = computed(
    () => this.canvasMode() && this.failedSrc() !== this.src(),
  );

  private readonly canvasEl = viewChild<ElementRef<HTMLCanvasElement>>('canvasEl');

  private abortController: AbortController | null = null;

  public constructor() {
    effect(() => {
      const useCanvas = this.showCanvas();
      const src = this.src();
      const canvas = this.canvasEl()?.nativeElement;

      this.cancelPlayback();

      if (!useCanvas || !src || !canvas) {
        return;
      }

      this.startGifPlayback(canvas, src);
    });

    this.destroyRef.onDestroy(() => this.cancelPlayback());
  }

  private async startGifPlayback(canvas: HTMLCanvasElement, src: string) {
    this.abortController = new AbortController();
    const rendered = await playGifInCanvas(canvas, src, this.abortController.signal, {
      maxLoops: REDUCED_MOTION_MAX_LOOPS,
      onDecoderFailure: error =>
        this.loggerService.warn(
          '[AnimatedImage] ImageDecoder path failed; falling back to still frame for the rest of the session',
          error,
        ),
      onFatalFailure: error => {
        this.loggerService.error('[AnimatedImage] Failed to render GIF', error);
        this.failedSrc.set(src);
      },
    });

    if (!rendered && !this.abortController?.signal.aborted) {
      this.failedSrc.set(src);
    }
  }

  private cancelPlayback() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}
